import React, { useEffect, useState } from "react";
import { closeModal, Modal, openModal } from "risa-oasis-ui_v2";
import { AddIcon } from "../../../svg/add-icon";
import "./AddStepDialog.scss";

interface AddStepDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (stepData: ProcessStepData) => void;
  loading?: boolean;
  insertIndex: number;
}

export interface ProcessStepData {
  type: string;
  name?: string;
  selector?: {
    xpath?: string;
    css?: string;
    id?: string;
    class?: string;
    text?: string;
  };
  value?: string;
  value_to_replace?: string;
  is_optional: boolean;
  seconds?: number;
  url?: string;
  meta?: Record<string, any>;
}

// Action types available for the process steps
const ACTION_TYPES = [
  { value: "navigate", label: "Navigate" },
  { value: "input", label: "Input" },
  { value: "click", label: "Click" },
  { value: "close_popup", label: "Close Popup" },
  { value: "wait", label: "Wait" },
  { value: "select", label: "Select" },
  { value: "auto_complete_select", label: "Auto Complete Select" },
  { value: "click_javascript", label: "Click JavaScript" },
  { value: "checkbox", label: "Checkbox" },
  { value: "hit_enter", label: "Hit Enter" },
  { value: "send_keys_globally", label: "Send Keys Globally" },
  { value: "safe_click", label: "Safe Click" },
  { value: "hit_refresh", label: "Hit Refresh" },
  { value: "handle_alert_popup", label: "Handle Alert Popup" },
  { value: "take_screenshot", label: "Take Screenshot" },
  { value: "scroll_and_click", label: "Scroll and Click Action Handler" },
];

// Use a constant dialogId
const ADD_STEP_DIALOG_ID = "add-step-dialog";

const AddStepDialog: React.FC<AddStepDialogProps> = ({
  isOpen,
  onClose,
  onConfirm,
  loading = false,
  insertIndex,
}) => {
  const [stepData, setStepData] = useState<ProcessStepData>({
    type: "",
    name: "",
    selector: {},
    value: "",
    value_to_replace: "",
    is_optional: false,
    seconds: undefined,
    url: "",
    meta: {},
  });

  // Open or close the modal when isOpen changes
  useEffect(() => {
    if (isOpen) {
      openModal(ADD_STEP_DIALOG_ID);
    } else {
      closeModal(ADD_STEP_DIALOG_ID);
    }
  }, [isOpen]);

  // Reset form when dialog opens
  useEffect(() => {
    if (isOpen) {
      setStepData({
        type: "",
        name: "",
        selector: {},
        value: "",
        value_to_replace: "",
        is_optional: false,
        seconds: undefined,
        url: "",
        meta: {},
      });
    }
  }, [isOpen]);

  const handleCancel = () => {
    onClose();
  };

  const handleSave = () => {
    // Clean up the data before sending
    const cleanedData: ProcessStepData = {
      type: stepData.type,
      is_optional: stepData.is_optional,
    };

    // Only include fields that have values
    if (stepData.name?.trim()) cleanedData.name = stepData.name.trim();
    if (stepData.value?.trim()) cleanedData.value = stepData.value.trim();
    if (stepData.value_to_replace?.trim())
      cleanedData.value_to_replace = stepData.value_to_replace.trim();
    if (stepData.url?.trim()) cleanedData.url = stepData.url.trim();
    if (stepData.seconds !== undefined && stepData.seconds > 0)
      cleanedData.seconds = stepData.seconds;

    // Handle selector object
    const hasSelector = Object.values(stepData.selector || {}).some((value) =>
      value?.trim(),
    );
    if (hasSelector) {
      cleanedData.selector = {};
      Object.entries(stepData.selector || {}).forEach(([key, value]) => {
        if (value?.trim()) {
          cleanedData.selector![key] = value.trim();
        }
      });
    }

    // Handle meta object - only include if it has meaningful data
    if (
      stepData.meta &&
      typeof stepData.meta === "object" &&
      Object.keys(stepData.meta).length > 0
    ) {
      cleanedData.meta = stepData.meta;
    }

    onConfirm(cleanedData);
  };

  const handleFieldChange = (field: keyof ProcessStepData, value: any) => {
    setStepData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSelectorChange = (selectorType: string, value: string) => {
    setStepData((prev) => ({
      ...prev,
      selector: {
        ...prev.selector,
        [selectorType]: value,
      },
    }));
  };

  const isFormValid = () => {
    return stepData.type.trim() !== "";
  };

  return (
    <Modal
      dialogId={ADD_STEP_DIALOG_ID}
      title={`Add New Process Step`}
      onCancel={handleCancel}
      onSave={handleSave}
      cancelText="Cancel"
      saveButtonText={loading ? "Adding..." : "Add Step"}
      disableSave={loading || !isFormValid()}
    >
      <div className="add-step-content">
        <div className="add-step-header">
          <AddIcon width="32" height="32" color="#0056d6" />
          <p className="add-step-description">
            Configure the new process step that will be inserted at position{" "}
            {insertIndex + 1}.
          </p>
        </div>

        <div className="add-step-form">
          {/* Type Field - Required */}
          <div className="form-group">
            <label className="form-label required">Action Type</label>
            <select
              value={stepData.type}
              onChange={(e) => handleFieldChange("type", e.target.value)}
              className="form-select"
              required
            >
              <option value="">Select action type...</option>
              {ACTION_TYPES.map((actionType) => (
                <option key={actionType.value} value={actionType.value}>
                  {actionType.label}
                </option>
              ))}
            </select>
          </div>

          {/* Name Field - Optional */}
          <div className="form-group">
            <label className="form-label">Step Name</label>
            <input
              type="text"
              value={stepData.name || ""}
              onChange={(e) => handleFieldChange("name", e.target.value)}
              className="form-input"
              placeholder="Enter step name (optional)"
            />
          </div>

          {/* Selector Fields - Optional */}
          <div className="form-group">
            <label className="form-label">Selector</label>
            <div className="selector-fields">
              <div className="selector-row">
                <div className="selector-field">
                  <label className="selector-label">XPath</label>
                  <input
                    type="text"
                    value={stepData.selector?.xpath || ""}
                    onChange={(e) =>
                      handleSelectorChange("xpath", e.target.value)
                    }
                    className="form-input"
                    placeholder="XPath selector"
                  />
                </div>
                <div className="selector-field">
                  <label className="selector-label">CSS</label>
                  <input
                    type="text"
                    value={stepData.selector?.css || ""}
                    onChange={(e) =>
                      handleSelectorChange("css", e.target.value)
                    }
                    className="form-input"
                    placeholder="CSS selector"
                  />
                </div>
              </div>
              <div className="selector-row">
                <div className="selector-field">
                  <label className="selector-label">ID</label>
                  <input
                    type="text"
                    value={stepData.selector?.id || ""}
                    onChange={(e) => handleSelectorChange("id", e.target.value)}
                    className="form-input"
                    placeholder="Element ID"
                  />
                </div>
                <div className="selector-field">
                  <label className="selector-label">Class</label>
                  <input
                    type="text"
                    value={stepData.selector?.class || ""}
                    onChange={(e) =>
                      handleSelectorChange("class", e.target.value)
                    }
                    className="form-input"
                    placeholder="Element class"
                  />
                </div>
              </div>
              <div className="selector-row">
                <div className="selector-field full-width">
                  <label className="selector-label">Text</label>
                  <input
                    type="text"
                    value={stepData.selector?.text || ""}
                    onChange={(e) =>
                      handleSelectorChange("text", e.target.value)
                    }
                    className="form-input"
                    placeholder="Element text content"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Value Field - Optional */}
          <div className="form-group">
            <label className="form-label">Value</label>
            <input
              type="text"
              value={stepData.value || ""}
              onChange={(e) => handleFieldChange("value", e.target.value)}
              className="form-input"
              placeholder="Enter value (optional)"
            />
          </div>

          {/* Value to Replace Field - Optional */}
          <div className="form-group">
            <label className="form-label">Value to Replace</label>
            <input
              type="text"
              value={stepData.value_to_replace || ""}
              onChange={(e) =>
                handleFieldChange("value_to_replace", e.target.value)
              }
              className="form-input"
              placeholder="Enter value to replace (optional)"
            />
          </div>

          {/* URL Field - Optional */}
          <div className="form-group">
            <label className="form-label">URL</label>
            <input
              type="url"
              value={stepData.url || ""}
              onChange={(e) => handleFieldChange("url", e.target.value)}
              className="form-input"
              placeholder="Enter URL (optional)"
            />
          </div>

          {/* Seconds Field - Optional */}
          <div className="form-group">
            <label className="form-label">Wait Seconds</label>
            <input
              type="number"
              min="0"
              step="1"
              value={stepData.seconds || ""}
              onChange={(e) =>
                handleFieldChange(
                  "seconds",
                  e.target.value ? parseInt(e.target.value) : undefined,
                )
              }
              className="form-input"
              placeholder="Wait time in seconds (optional)"
            />
          </div>

          {/* Is Optional Field - Boolean */}
          <div className="form-group">
            <label className="form-label checkbox-label">
              <input
                type="checkbox"
                checked={stepData.is_optional}
                onChange={(e) =>
                  handleFieldChange("is_optional", e.target.checked)
                }
                className="form-checkbox"
              />
              This step is optional
            </label>
          </div>
        </div>

        <div className="add-step-warning">
          <div className="warning-icon">ℹ️</div>
          <div className="warning-text">
            <strong>Note:</strong> The new step will be inserted at position{" "}
            {insertIndex + 1} in the process configuration. All subsequent steps
            will be shifted down.
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default AddStepDialog;
